import { Button, Select, TextInput, Textarea, Tooltip } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  fetchClientTypes,
  fetchCountries,
  fetchExistingEmails,
  fetchMarketSegments,
  fetchMarketSubSegments,
  fetchStates,
} from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { UserLevelNames } from '../../Models/UserLevel';
import { HiEye } from 'react-icons/hi';
import ClientApi from '../../Api/ClientApi';
import { Client } from '../../Models/Client';
import { SelectOption } from '../../Models/SelectOption';

export default function ClientProfileEdit() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    states,
    countries,
    existingEmails,
    clientTypes,
    marketSegments,
    marketSubSegments,
  } = useAppSelector((state) => state.commonFields);
  const { userModel } = useAppSelector((state) => state.user);
  const [clientData, setClientData] = useState<Client | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const [formData, setFormData] = useState<Client>({
    id: 0,
    name: null,
    clientTypeId: null,
    marketSubSegmentId: null,
    address: null,
    city: null,
    stateId: null,
    zip: null,
    phone: null,
    email: null,
    description: null,
    website: null,
    accountNumber: null,
    quickbooksId: null,
    dateCreated: null,
    countryId: null,

    clientLocations: null,
    clientType: null,
    marketSubSegment: null,
    state: null,
    country: null,
  });

  const getClient = () => {
    if (id) {
      setIsLoading(true);
      ClientApi.getClient(id)
        .then((res) => {
          setClientData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      setClientData(null);
    }
  };

  const getReferences = async () => {
    if (
      !states.length ||
      !countries.length ||
      !existingEmails.length ||
      !clientTypes.length ||
      !marketSegments.length ||
      !marketSubSegments.length
    ) {
      await Promise.all([
        dispatch(fetchExistingEmails()),
        dispatch(fetchStates()),
        dispatch(fetchCountries()),
        dispatch(fetchClientTypes()),
        dispatch(fetchMarketSegments()),
        dispatch(fetchMarketSubSegments()),
      ]);
      //   setIsLoading(false);
    } else {
      //   setIsLoading(false);
    }
  };

  useEffect(() => {
    getClient();
  }, [id]);

  useEffect(() => {
    if (clientData && userModel && id) {
      if (userModel.userLevelId !== UserLevelNames.Admin) {
        if (userModel.id !== clientData.id) {
          toast.error('You do not have access to this page');
          navigate(-1);
        }
      }
    }
    handleSetInitialValues();
  }, [clientData]);

  useEffect(() => {
    getReferences();
  }, []);

  const handleSetInitialValues = () => {
    const values = clientData
      ? {
          ...clientData,
        }
      : {
          id: 0,
          name: null,
          clientTypeId: null,
          marketSubSegmentId: null,
          address: null,
          city: null,
          stateId: null,
          zip: null,
          phone: null,
          email: null,
          description: null,
          website: null,
          accountNumber: null,
          quickbooksId: null,
          dateCreated: null,
          countryId: null,

          clientLocations: null,
          clientType: null,
          marketSubSegment: null,
          state: null,
          country: null,
        };

    setFormData(values as Client);
  };

  const checkEmail = (email: string) => {
    if (
      clientData?.email &&
      email.trim().toLowerCase() !== clientData?.email.trim().toLowerCase() &&
      existingEmails.some((x) => x === email.trim().toLowerCase())
    ) {
      setButtonDisabled(true);
      setEmailExists(true);
    } else {
      setButtonDisabled(false);
      setEmailExists(false);
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    // if (name === 'email') {
    //   checkEmail(value);
    // }
  };

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'countryId') {
      setFormData((prevData) => ({ ...prevData, stateId: null }));
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const filteredStates = useMemo(() => {
    const list = states
      .filter((x) => x.countryId === Number(formData.countryId ?? 0))
      .map((t) => ({
        label: t.name,
        value: t.id!,
      })) as SelectOption[];
    return [...list];
  }, [formData.countryId]);

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    if (!formData.clientTypeId) {
      formData.clientTypeId = null;
    }
    if (!formData.marketSubSegmentId) {
      formData.marketSubSegmentId = null;
    }
    if (!formData.stateId) {
      formData.stateId = null;
    }
    if (!formData.countryId) {
      formData.countryId = null;
    }
    try {
      setIsLoading(true);
      return ClientApi.addUpdateClient(formData)
        .then((res) => {
          if (res.data.success) {
            toast.success('Saved');
            setIsLoading(false);
            setButtonDisabled(false);
            navigate(`/client/${formData?.id}`);
          } else {
            setButtonDisabled(false);
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          console.error(err);
          toast.error('Error: ' + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  return (
    <div className='w-full'>
      <div className='w-full p-4'>
        <div className='mx-1 flex items-end'>
          <div className='flex flex-1'>
            <h1 className='text-gray-900 font-bold text-2xl lg:text-4xl'>
              Edit Client Information
            </h1>
          </div>
          <div className='right-2'>
            <Link to={`/client/${clientData?.id}`}>
              <Tooltip content='View'>
                <Button className='buttonMain'>
                  <HiEye size={20} />
                </Button>
              </Tooltip>
            </Link>
          </div>
        </div>
      </div>

      <div className='h-[calc(100vh-125px)] overflow-y-auto'>
        <div className='container mx-auto p-2'>
          <div className='md:flex no-wrap'>
            <div className='w-full mx-2'>
              <div className='bg-white p-3'>
                <div className='flex items-center space-x-2 font-bold text-gray-500 leading-8 py-2'>
                  <span className='tracking-wide uppercase text-xl lg:text-3xl'>
                    {clientData?.name}
                  </span>
                </div>
                <hr className='bg-gray-300 h-[5px]'></hr>
                <form id='editUser' onSubmit={onSubmit}>
                  <div className='text-gray-500'>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Name:</div>
                      </div>
                      <div className='flex'>
                        <div className='w-full'>
                          <TextInput
                            id='name'
                            name='name'
                            type='text'
                            placeholder='Name...'
                            onChange={handleChangeInput}
                            value={formData.name ?? ''}
                            sizing='md'
                          />
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Account #:
                        </div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='accountNumber'
                          name='accountNumber'
                          type='text'
                          placeholder='Account #...'
                          onChange={handleChangeInput}
                          value={formData.accountNumber ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Type:</div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='clientTypeId'
                          name='clientTypeId'
                          onChange={handleChangeSelect}
                          value={formData.clientTypeId ?? ''}
                          sizing='md'
                          disabled={
                            userModel?.userLevelId !== UserLevelNames.Admin
                          }
                        >
                          <option value={''}>(Select)</option>
                          {clientTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Market Segment:
                        </div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='marketSegmentId'
                          name='marketSegmentId'
                          onChange={handleChangeSelect}
                          value={formData.marketSubSegmentId ?? ''}
                          sizing='md'
                          disabled={
                            userModel?.userLevelId !== UserLevelNames.Admin
                          }
                        >
                          <option value={''}>(Select)</option>
                          {marketSegments.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Market Sub-Segment:
                        </div>
                      </div>
                      <div className='w-full'>
                        <Select
                          id='marketSubSegmentId'
                          name='marketSubSegmentId'
                          onChange={handleChangeSelect}
                          value={formData.marketSubSegmentId ?? ''}
                          sizing='md'
                          disabled={
                            userModel?.userLevelId !== UserLevelNames.Admin
                          }
                        >
                          <option value={''}>(Select)</option>
                          {marketSubSegments.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </Select>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Email:</div>
                      </div>
                      <div className='flex'>
                        <div className='w-full'>
                          <TextInput
                            id='email'
                            name='email'
                            type='text'
                            placeholder='Email...'
                            onChange={handleChangeInput}
                            value={formData.email ?? ''}
                            sizing='md'
                            required
                          />
                        </div>
                        {/* {emailExists && (
                          <div className='flex w-2/3 text-red-600'>
                            <div className='content-center px-2'>
                              <HiInformationCircle />
                            </div>
                            <div className='content-center'>
                              Email already exists
                            </div>
                          </div>
                        )} */}
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Phone:</div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='phone'
                          name='phone'
                          type='text'
                          placeholder='Phone...'
                          onChange={handleChangeInput}
                          value={formData.phone ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Address:</div>
                      </div>
                      <div className='flex flex-col'>
                        <div className='w-1/3'>
                          <Select
                            id='countryId'
                            name='countryId'
                            onChange={handleChangeSelect}
                            value={formData.countryId ?? ''}
                            sizing='md'
                          >
                            <option value=''>(Country)</option>
                            {countries.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                          </Select>
                        </div>
                        <div className='w-full'>
                          <TextInput
                            id='address'
                            name='address'
                            type='text'
                            placeholder='Street...'
                            onChange={handleChangeInput}
                            value={formData.address ?? ''}
                            sizing='md'
                          />
                        </div>
                        <div className='w-full flex'>
                          <div className='w-1/3'>
                            <TextInput
                              id='city'
                              name='city'
                              type='text'
                              placeholder='City...'
                              onChange={handleChangeInput}
                              value={formData.city ?? ''}
                              sizing='md'
                            />
                          </div>
                          <div className='w-1/3'>
                            <Select
                              id='stateId'
                              name='stateId'
                              onChange={handleChangeSelect}
                              value={formData.stateId ?? ''}
                              sizing='md'
                              disabled={Number(formData.countryId ?? 0) <= 0}
                              helperText={
                                Number(formData.countryId ?? 0) <= 0
                                  ? 'Select Country'
                                  : ''
                              }
                            >
                              <option value=''>(State)</option>
                              {filteredStates.map((state) => (
                                <option key={state.value} value={state.value}>
                                  {state.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                          <div className='w-1/3'>
                            <TextInput
                              id='zip'
                              name='zip'
                              type='text'
                              placeholder='Zip...'
                              onChange={handleChangeInput}
                              value={formData.zip ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>Website:</div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          id='website'
                          name='website'
                          type='text'
                          placeholder='Website...'
                          onChange={handleChangeInput}
                          value={formData.website ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Quickbooks Id:
                        </div>
                      </div>
                      <div className='w-full'>
                        <TextInput
                          disabled={true}
                          id='quickbooksId'
                          name='quickbooksId'
                          type='text'
                          placeholder='Quickbooks Id...'
                          onChange={handleChangeInput}
                          value={formData.quickbooksId ?? ''}
                          sizing='md'
                        />
                      </div>
                    </div>
                    <hr className='bg-gray-300 h-[2px]'></hr>
                    <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] text-sm md:text-xl'>
                      <div className='flex'>
                        <div className='font-bold content-center'>
                          Description:
                        </div>
                      </div>
                      <div>
                        <Textarea
                          id='description'
                          name='description'
                          placeholder='Description...'
                          onChange={handleChangeTextArea}
                          value={formData.description ?? ''}
                          rows={4}
                          autoComplete='new-password'
                        />
                      </div>
                    </div>
                  </div>
                </form>
                <div className={`flex justify-end pt-2`}>
                  <Button
                    disabled={buttonDisabled}
                    type='submit'
                    className='buttonMain'
                    form='editUser'
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
